interface Endereco {
  id?: number;
  rua: string;
  numero_casa: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

interface ResultadoFrete {
  distancia: number; // em km
  valorFrete: number;
  enderecoFormatado: string;
  tempoEstimado: string; // tempo estimado de entrega
  cep?: string;
}

class FreteService {
  // Valores padrão (serão substituídos pelas configurações do backend)
  private readonly FRETE_MINIMO = 5.0;
  private readonly VALOR_POR_KM = 1.0;

  /**
   * Calcula o frete baseado na distância entre o endereço base e o endereço do cliente
   */
  async calcularFrete(enderecoCliente: Endereco, empresaId: number = 1): Promise<ResultadoFrete> {
    try {
      // Validação prévia de campos essenciais para evitar disparos desnecessários
      if (!enderecoCliente?.rua || !enderecoCliente?.numero_casa || !enderecoCliente?.bairro || !enderecoCliente?.cep) {
        throw new Error('Endereço incompleto para cálculo');
      }

      // Chamar a API do backend para calcular o frete
      const response = await fetch('/api/frete/calcular', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...enderecoCliente,
          empresa_id: empresaId
        })
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        if (data?.dados?.foraDaArea) {
          throw new Error(`Infelizmente não estamos disponíveis no seu local. Distância: ${data.dados.distancia}km`);
        }
        throw new Error(data?.erro || 'Erro ao calcular frete');
      }

      if (data && data.sucesso) {
        return {
          distancia: Number(data.dados.distancia) || 0,
          valorFrete: Number(data.dados.valorFrete) || 0,
          enderecoFormatado: data.dados.enderecoFormatado,
          tempoEstimado: data.dados.tempoEstimado
        };
      } else {
        if (data?.dados?.foraDaArea) {
          throw new Error(`Infelizmente não estamos disponíveis no seu local. Distância: ${data.dados.distancia}km`);
        }
        throw new Error(data?.erro || 'Erro ao calcular frete');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Não foi possível calcular o frete.';
      throw new Error(msg);
    }
  }

  /**
   * Obtém informações sobre a política de frete (dinâmico por empresa)
   */
  async getInfoFrete(empresaId: number = 1): Promise<{ minimo: number; valorPorKm: number; areaMaxima: number }> {
    try {
      const url = `/api/frete/info${empresaId ? `?empresa_id=${empresaId}` : ''}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Erro ao obter informações do frete');
      }

      const data = await response.json();
      
      if (data && data.sucesso) {
        return {
          minimo: Number(data.dados.minimo) || this.FRETE_MINIMO,
          valorPorKm: Number(data.dados.valorPorKm) || this.VALOR_POR_KM,
          areaMaxima: Number(data.dados.areaMaxima) || 25
        };
      } else {
        throw new Error(data?.erro || 'Erro ao obter informações do frete');
      }
    } catch (error) {
      // Retornar valores padrão em caso de erro silenciosamente
      return {
        minimo: this.FRETE_MINIMO,
        valorPorKm: this.VALOR_POR_KM,
        areaMaxima: 25
      };
    }
  }
}

export default FreteService;
export type { Endereco, ResultadoFrete };
